import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgId } from '../auth';
import { toast } from "@/hooks/use-toast";
import { queryConfigs } from '@/lib/queryClient';

export interface Tag {
  id: string;
  name: string;
  color: string;
  org_id: string;
}

export const useTags = () => {
  const orgId = useOrgId();

  return useQuery({
    ...queryConfigs.structural,
    queryKey: ["tags", orgId],
    queryFn: async () => {
      if (!orgId) throw new Error("No organization ID");

      const { data, error } = await supabase
        .from("tags")
        .select("*")
        .eq("org_id", orgId)
        .order("name");

      if (error) throw error;
      return data as Tag[];
    },
    enabled: !!orgId,
  });
};

export const useCreateTag = () => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation({
    mutationFn: async (data: { name: string; color: string }) => {
      if (!orgId) throw new Error("No organization ID");

      const { data: tag, error } = await supabase
        .from("tags")
        .insert([{ ...data, org_id: orgId }])
        .select()
        .single();

      if (error) throw error;
      return tag;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      toast({
        title: "Tag criada",
        description: "A tag foi criada com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao criar tag",
        description: error.message || "Ocorreu um erro ao criar a tag.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateTag = () => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation({
    mutationFn: async ({ id, name, color }: { id: string; name: string; color: string }) => {
      if (!orgId) throw new Error("No organization ID");

      const { data: tag, error } = await supabase
        .from("tags")
        .update({ name, color })
        .eq("id", id)
        .eq("org_id", orgId)
        .select()
        .single();

      if (error) throw error;
      return tag;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["deals-by-stage"] });
      queryClient.invalidateQueries({ queryKey: ["deal-tags"] });
      toast({
        title: "Tag atualizada",
        description: "A tag foi atualizada com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar tag",
        description: error.message || "Ocorreu um erro ao atualizar a tag.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteTag = () => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation({
    mutationFn: async (tagId: string) => {
      if (!orgId) throw new Error("No organization ID");

      const { error } = await supabase
        .from("tags")
        .delete()
        .eq("id", tagId)
        .eq("org_id", orgId);

      if (error) throw error;
      return tagId;
    },
    onSuccess: () => {
      // Cascade do FK deal_tags.tag_id → tags(id) ON DELETE CASCADE
      // já limpa o banco; frontend só precisa refetch para esquecer.
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["deals-by-stage"] });
      queryClient.invalidateQueries({ queryKey: ["deal-tags"] });
      toast({
        title: "Tag excluída",
        description: "A tag foi removida de todos os negócios.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao excluir tag",
        description: error.message || "Ocorreu um erro ao excluir a tag.",
        variant: "destructive",
      });
    },
  });
};

export const useDealTags = (dealId: string | undefined) => {
  const orgId = useOrgId();

  return useQuery({
    ...queryConfigs.structural,
    queryKey: ["deal-tags", dealId],
    queryFn: async () => {
      if (!dealId || !orgId) return [];

      const { data, error } = await supabase
        .from("deal_tags")
        .select(`
          tag_id,
          tags (
            id,
            name,
            color
          )
        `)
        .eq("deal_id", dealId)
        .eq("org_id", orgId);

      if (error) throw error;
      return (data?.map((dt: any) => dt.tags).filter(Boolean) || []) as Tag[];
    },
    enabled: !!dealId && !!orgId,
  });
};

export const useUpdateDealTags = () => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation({
    onMutate: async ({ dealId, tagIds }: { dealId: string; tagIds: string[] }) => {
      await queryClient.cancelQueries({ queryKey: ['deals-by-stage'] });

      const snapshots = queryClient.getQueriesData({ queryKey: ['deals-by-stage'] });

      const allTagsCache = (queryClient.getQueryData<Tag[]>(['tags', orgId]) ?? []);
      const newTags = allTagsCache.filter((t) => tagIds.includes(t.id));

      queryClient.setQueriesData(
        { queryKey: ['deals-by-stage'] },
        (old: any) => {
          if (!old?.deals) return old;
          return {
            ...old,
            deals: old.deals.map((d: any) =>
              d.id === dealId ? { ...d, tags: newTags } : d
            ),
          };
        }
      );

      return { snapshots };
    },
    mutationFn: async ({
      dealId,
      tagIds,
    }: {
      dealId: string;
      tagIds: string[];
    }) => {
      if (!orgId) throw new Error("No organization ID");

      const { data: currentRows, error: fetchError } = await supabase
        .from("deal_tags")
        .select("tag_id")
        .eq("deal_id", dealId)
        .eq("org_id", orgId);
      if (fetchError) throw fetchError;

      const currentIds = new Set((currentRows ?? []).map((r) => r.tag_id as string));
      const desiredIds = new Set(tagIds);

      const toAdd = tagIds.filter((id) => !currentIds.has(id));
      const toRemove = [...currentIds].filter((id) => !desiredIds.has(id));

      if (toRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from("deal_tags")
          .delete()
          .eq("deal_id", dealId)
          .eq("org_id", orgId)
          .in("tag_id", toRemove);
        if (deleteError) throw deleteError;
      }

      if (toAdd.length > 0) {
        const { error: insertError } = await supabase.from("deal_tags").insert(
          toAdd.map((tagId) => ({
            deal_id: dealId,
            tag_id: tagId,
            org_id: orgId,
          }))
        );
        if (insertError) throw insertError;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["deal-tags", variables.dealId] });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      // Targeted update instead of invalidateQueries(['deals-by-stage']):
      // broad invalidation triggers a PostgREST refetch of all stage columns,
      // where the deal_tags embed can fail silently and wipe every deal's tags.
      const allTagsCache = (queryClient.getQueryData<Tag[]>(['tags', orgId]) ?? []);
      const newTags = allTagsCache.filter((t) => variables.tagIds.includes(t.id));
      queryClient.setQueriesData(
        { queryKey: ['deals-by-stage'] },
        (old: any) => {
          if (!old?.deals) return old;
          return {
            ...old,
            deals: old.deals.map((d: any) =>
              d.id === variables.dealId ? { ...d, tags: newTags } : d
            ),
          };
        }
      );
      queryClient.invalidateQueries({ queryKey: ["deal-events", variables.dealId] });
      toast({
        title: "Tags atualizadas",
        description: "As tags do negócio foram atualizadas com sucesso.",
      });
    },
    onError: (error: any, _vars: any, context: any) => {
      context?.snapshots?.forEach(([key, data]: [any, any]) => {
        queryClient.setQueryData(key, data);
      });
      toast({
        title: "Erro ao atualizar tags",
        description: error.message || "Ocorreu um erro ao atualizar as tags.",
        variant: "destructive",
      });
    },
  });
};
